// ABOUTME: Evidence viewer component with filtering and search capabilities
// ABOUTME: Displays full evidence data with clickable modal for interview sources

import React, { useState, useMemo } from 'react';
import { EvidenceSourceModal } from '@/components/evidence/EvidenceSourceModal';

interface EvidenceItem {
  hypothesis_id?: string;
  evidence_id?: string;
  evidence_type?: string;
  stance?: string;
  type?: string;
  quote?: string;
  answer_text?: string;
  reasoning?: string;
  strength?: number | string;
  persona?: string;
  institution?: string;
  interview_id?: string;
  significance?: string;  // Only present in critical evidence
}

interface EvidenceViewerProps {
  evidenceItems?: EvidenceItem[];
  className?: string;
}

export const EvidenceViewer: React.FC<EvidenceViewerProps> = ({
  evidenceItems = [],
  className = ''
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterPersona, setFilterPersona] = useState<string>('all');
  const [filterStrength, setFilterStrength] = useState<string>('all');
  const [selectedEvidenceId, setSelectedEvidenceId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleEvidenceClick = (evidenceId: string | undefined) => {
    if (evidenceId) {
      setSelectedEvidenceId(evidenceId);
      setIsModalOpen(true);
    }
  };

  // Extract unique values for filters
  const personas = useMemo(() => {
    const uniquePersonas = new Set(evidenceItems.map(item => item.persona).filter(Boolean));
    return Array.from(uniquePersonas).sort();
  }, [evidenceItems]);

  const types = useMemo(() => {
    console.log('Total evidence items:', evidenceItems.length);
    console.log('Sample evidence item:', evidenceItems[0]);

    const typeValues = evidenceItems.map(item => {
      // Check multiple possible field names for evidence type
      return item.stance || item.evidence_type || item.type;
    }).filter(Boolean);

    const uniqueTypes = new Set(typeValues);
    const sortedTypes = Array.from(uniqueTypes).sort();
    console.log('Available evidence types:', sortedTypes);
    return sortedTypes;
  }, [evidenceItems]);

  // Filter evidence based on criteria
  const filteredEvidence = useMemo(() => {
    return evidenceItems.filter(item => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          item.quote?.toLowerCase().includes(searchLower) ||
          item.answer_text?.toLowerCase().includes(searchLower) ||
          item.reasoning?.toLowerCase().includes(searchLower) ||
          item.persona?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Type filter
      if (filterType !== 'all') {
        const itemType = item.stance || item.evidence_type || item.type;
        if (itemType !== filterType) return false;
      }

      // Persona filter
      if (filterPersona !== 'all' && item.persona !== filterPersona) {
        return false;
      }

      // Strength filter
      if (filterStrength !== 'all') {
        const itemStrength = typeof item.strength === 'string' ?
          item.strength.toLowerCase() :
          (item.strength?.toString() || '');
        if (itemStrength !== filterStrength.toLowerCase()) {
          return false;
        }
      }

      return true;
    });
  }, [evidenceItems, searchTerm, filterType, filterPersona, filterStrength]);

  const getStanceColor = (_stance?: string) => {
    // All cards use grey background - stance is shown via pill badge only
    return 'border-gray-300 bg-gray-50';
  };

  const getStanceBadge = (stance?: string) => {
    switch (stance?.toUpperCase()) {
      case 'SUPPORT':
      case 'SUPPORTS':
        return 'bg-green-100 text-green-800';
      case 'REFUTE':
      case 'REFUTES':
        return 'bg-red-100 text-red-800';
      case 'MIXED':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!evidenceItems || evidenceItems.length === 0) {
    return (
      <div className="text-gray-500 text-center py-8">
        No evidence items available
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Filters */}
      <div className="bg-white rounded-lg p-4 mb-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Evidence
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search quotes, reasoning..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Evidence Type
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All Types</option>
              {types.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Group Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Group
            </label>
            <select
              value={filterPersona}
              onChange={(e) => setFilterPersona(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All Groups</option>
              {personas.map(persona => (
                <option key={persona} value={persona}>{persona}</option>
              ))}
            </select>
          </div>

          {/* Strength Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Strength
            </label>
            <select
              value={filterStrength}
              onChange={(e) => setFilterStrength(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All Strengths</option>
              <option value="very weak">Very Weak</option>
              <option value="weak">Weak</option>
              <option value="moderate">Moderate</option>
              <option value="strong">Strong</option>
              <option value="very strong">Very Strong</option>
            </select>
          </div>
        </div>

        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredEvidence.length} of {evidenceItems.length} evidence items
        </div>
      </div>

      {/* Evidence Items */}
      <div className="space-y-4 max-h-[800px] overflow-y-auto">
        {filteredEvidence.map((item, index) => (
          <div
            key={item.evidence_id || index}
            className={`border-l-4 rounded-lg p-4 relative ${getStanceColor(item.stance || item.evidence_type)} ${
              item.evidence_id ? 'cursor-pointer hover:shadow-lg transition-all' : ''
            }`}
            onClick={() => item.evidence_id && handleEvidenceClick(item.evidence_id)}
            title={item.evidence_id ? 'Click to view full interview transcript' : ''}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex flex-wrap items-center gap-2">
                {(item.stance || item.evidence_type || item.type) && (
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${getStanceBadge(item.stance || item.evidence_type || item.type)}`}>
                    {item.stance || item.evidence_type || item.type}
                  </span>
                )}
                {item.persona && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                    {item.persona}
                  </span>
                )}
                {item.institution && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                    {item.institution}
                  </span>
                )}
                {item.interview_id && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                    Interview: {item.interview_id}
                  </span>
                )}
                {/* Show validity badge if this is from critical evidence */}
                {item.significance && (
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    item.evidence_id ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                  }`}>
                    {item.evidence_id ? 'Valid' : 'Unmatched'}
                  </span>
                )}
              </div>
              {item.strength && (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">Strength:</span>
                  {(() => {
                    // Convert string strength to numeric value
                    let numericStrength: number = typeof item.strength === 'number' ? item.strength : 3;
                    if (typeof item.strength === 'string') {
                      const strengthMap: { [key: string]: number } = {
                        'very weak': 1,
                        'weak': 2,
                        'moderate': 3,
                        'strong': 4,
                        'very strong': 5
                      };
                      numericStrength = strengthMap[item.strength.toLowerCase()] || 3;
                    }

                    return (
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map(i => (
                          <span
                            key={i}
                            className={`text-xs ${i <= numericStrength ? 'text-yellow-500' : 'text-gray-300'}`}
                          >
                            ★
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Quote */}
            {(item.quote || item.answer_text) && (
              <blockquote className="italic text-gray-700 bg-white rounded p-3 mb-3">
                "{item.quote || item.answer_text}"
              </blockquote>
            )}

            {/* Reasoning */}
            {item.reasoning && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">Analysis:</span> {item.reasoning}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Evidence Source Modal */}
      <EvidenceSourceModal
        evidenceId={selectedEvidenceId}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};
